import { useEffect, useMemo, useState } from "react";
import { Check, Clock, X, Activity } from "lucide-react";
import api from "@/lib/api";

/**
 * Iter 144 · DS v2 — Timeline horizontal de estados de una reparación.
 *
 * Usa `GET /api/repairs/{id}/status-timeline` que agrega los períodos de
 * cada estado a partir del `repair.timeline[]` existente.
 * Al hacer clic en un círculo se abre un popover con los detalles.
 */

// Iter 162 — Tiempo LABORAL: 1 día = jornada (10 h por defecto), no 24 h.
const formatDuration = (ms, dayHours = 10) => {
  if (ms == null || ms <= 0) return "0 h";
  const totalMin = Math.floor(ms / 60000);
  const dayMin = Math.max(1, Math.round(dayHours * 60));
  const days = Math.floor(totalMin / dayMin);
  const hours = Math.floor((totalMin % dayMin) / 60);
  const mins = totalMin % 60;
  const parts = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? "día" : "días"}`);
  if (hours > 0) parts.push(`${hours} h`);
  if (days === 0 && hours < 2 && mins > 0) parts.push(`${mins} m`);
  return parts.join(" ") || "0 h";
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      timeZone: "Europe/Madrid",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function RepairStatusTimeline({ repairId }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  // Iter 162 — El servidor calcula el tiempo laboral hasta "ahora"; se refresca
  // cada minuto (el contador se congela solo fuera del horario laboral).
  useEffect(() => {
    if (!repairId) return;
    const load = () => api.get(`/repairs/${repairId}/status-timeline`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [repairId]);

  const liveExtraMs = 0;
  const dayHours = data?.work_day_hours || 10;
  const fmt = (ms) => formatDuration(ms, dayHours);

  // Progreso porcentual de la barra: nodos completados / total.
  const progressPct = useMemo(() => {
    if (!data) return 0;
    const total = data.statuses.length;
    if (total === 0) return 0;
    const reachedIdx = data.statuses.findIndex((s) => s.is_current);
    // Si no hay actual y todos completados → 100%
    const anyCurrent = reachedIdx >= 0;
    if (!anyCurrent) {
      const past = data.statuses.filter((s) => s.is_past).length;
      return past === total ? 100 : (past / total) * 100;
    }
    // Con actual, ubica en el centro del step actual.
    return ((reachedIdx + 0.5) / total) * 100;
  }, [data]);

  if (!data) return null;

  return (
    <div className="ds-card ds-timeline-glow mt-6 p-6 lg:p-7" data-testid="repair-status-timeline" style={{ boxShadow: "var(--shadow-md)" }}>
      {/* HEAD */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="font-display font-black text-[13px] uppercase tracking-[.12em] text-[#4B5158] inline-flex items-center gap-3">
          <span className="ds-ic-tile"><Activity size={14}/></span>
          Cronología del estado
        </div>
        <div className="flex gap-4 text-[11px] text-[#8A9098]">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-[10px] h-[10px] rounded-full" style={{ background: "linear-gradient(160deg,#3a4a26,#1e2814)" }}/>
            Completado
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-[10px] h-[10px] rounded-full" style={{ background: "linear-gradient(160deg,#8AAF4E,#6E8B3D)" }}/>
            Estado actual
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-[10px] h-[10px] rounded-full bg-[#D5D7CE]"/>
            Pendiente
          </span>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row xl:items-start xl:gap-8">
        {/* Timeline horizontal */}
        <div className="flex-1 overflow-x-auto pb-2">
          <div className="relative min-w-[820px] pt-2">
            {/* Barra de progreso */}
            <div className="absolute left-[26px] right-[26px] top-[27px] h-[4px] rounded-full bg-[#EEF0EA] overflow-hidden">
              <div
                className="absolute left-0 top-0 bottom-0 rounded-full transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  background: "linear-gradient(90deg,#8AAF4E,#6E8B3D)",
                  boxShadow: "0 0 0 3px rgba(110,139,61,.1)",
                }}
              />
            </div>
            <div className="grid relative z-10" style={{ gridTemplateColumns: `repeat(${data.statuses.length}, minmax(0, 1fr))` }}>
              {data.statuses.map((s) => {
                const isPast = s.is_past;
                const isCurrent = s.is_current;
                const totalMsLive = isCurrent && !data.frozen_at ? s.total_ms + liveExtraMs : s.total_ms;
                let circleStyle = {};
                let circleCls = "relative w-[52px] h-[52px] rounded-full grid place-items-center font-display font-black text-[14px] border-[3px] border-white transition-transform ";
                if (isCurrent) {
                  circleCls += "text-white ds-pulse-ring";
                  circleStyle = {
                    background: "linear-gradient(160deg,#8AAF4E,#6E8B3D)",
                    boxShadow: "0 6px 20px rgba(110,139,61,.4),0 0 0 6px rgba(110,139,61,.15)",
                  };
                } else if (isPast) {
                  circleCls += "text-white";
                  circleStyle = {
                    background: "linear-gradient(160deg,#3a4a26,#1e2814)",
                    boxShadow: "0 4px 12px rgba(30,40,20,.3)",
                  };
                } else {
                  circleCls += "text-[#8A9098] bg-white";
                  circleStyle = { borderColor: "#E6E7E1" };
                }

                return (
                  <button
                    key={s.status}
                    type="button"
                    onClick={() => s.is_reached && setSelected(s)}
                    disabled={!s.is_reached}
                    data-testid={`timeline-node-${s.status}`}
                    className={`relative flex flex-col items-center gap-2 px-1 disabled:cursor-default ${s.is_reached ? "cursor-pointer group hover:-translate-y-0.5 transition-transform" : ""}`}
                  >
                    <span className={circleCls} style={circleStyle}>
                      {isPast ? <Check size={18} strokeWidth={2.6}/> :
                       (isCurrent ? <Clock size={18} strokeWidth={2.6}/> : s.order + 1)}
                    </span>
                    <span className={`text-[10.5px] uppercase tracking-[.12em] font-bold text-center ${
                      isCurrent ? "text-[#3F5222]" : isPast ? "text-[#0F1210]" : "text-[#8A9098]"
                    }`}>
                      {s.label}
                    </span>
                    <span className={`text-[11px] font-mono text-center ${
                      isCurrent ? "text-[#3F5222] font-extrabold" : isPast ? "text-[#4B5158] font-semibold" : "text-[#8A9098] font-medium"
                    }`}>
                      {s.is_reached ? fmt(totalMsLive) : "Pendiente"}
                    </span>
                    {isCurrent && !data.frozen_at && (
                      <span className="mt-0.5 text-[9px] bg-[#6E8B3D] text-white px-2 py-[1px] rounded-full font-bold uppercase tracking-[.1em]">
                        Actual
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tiempo total */}
        <div className="mt-6 xl:mt-0 xl:pl-8 xl:border-l xl:border-dashed xl:border-[#D5D7CE] text-center xl:text-left shrink-0 min-w-[200px]" data-testid="timeline-total">
          <div className="font-mono text-[10.5px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
            Tiempo total
          </div>
          <div
            className="mt-2 font-display font-black text-[36px] leading-none tracking-tight"
            style={{ background: "linear-gradient(160deg,#0F1210,#3a3f34)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}
          >
            {fmt(data.total_ms)}
          </div>
          <div className="mt-1 text-[11px] text-[#8A9098]" data-testid="timeline-business-note">
            Tiempo laboral · {data.schedule?.start || "10:00"}–{data.schedule?.end || "20:00"} L–V · 1 día = {dayHours} h
          </div>
          {data.frozen_at ? (
            <div className="mt-1 text-[11px] text-[#8A9098]">
              Congelado el {formatDateTime(data.frozen_at)}
            </div>
          ) : !data.is_working_now && (
            <div className="mt-1 text-[11px] text-[#8A9098]" data-testid="timeline-paused">
              En pausa fuera del horario laboral
            </div>
          )}
        </div>
      </div>

      {/* Popover con detalles */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="ds-card max-w-lg w-full" style={{ boxShadow: "var(--shadow-lg)" }} onClick={(e) => e.stopPropagation()}>
            <div className="ds-card-h">
              <div>
                <div className="font-display font-black text-[15px] tracking-tight">
                  {selected.label} <span className="text-[#8A9098] font-mono text-[12px] font-medium">— {fmt(selected.total_ms)}</span>
                </div>
                <div className="text-[10.5px] text-[#8A9098] mt-0.5">
                  {selected.visits} visita{selected.visits !== 1 ? "s" : ""} · acumulado {fmt(selected.total_ms)} laborales
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 rounded-md hover:bg-[#F5F6F2] text-[#4B5158]"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto" data-testid="timeline-detail-list">
              {selected.periods.map((p, i) => (
                <div key={i} className="border border-[#E6E7E1] bg-[#FBFBF8] rounded-lg p-3 text-xs">
                  <div className="flex justify-between font-mono">
                    <span><strong className="text-[#0F1210]">Período {i + 1}</strong></span>
                    <span className="text-[#3F5222] font-bold">{fmt(p.ms)}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-[#4B5158]">
                    Entrada: <strong className="text-[#0F1210]">{formatDateTime(p.entry)}</strong>
                  </div>
                  <div className="text-[11px] text-[#4B5158]">
                    Salida: <strong className="text-[#0F1210]">{p.exit ? formatDateTime(p.exit) : <span className="text-[#3F5222]">— actual</span>}</strong>
                  </div>
                  <div className="text-[11px] text-[#4B5158]">
                    Usuario: <strong className="text-[#0F1210]">{p.by || "—"}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
